import type {
  ActiveJob,
  AgentProfile,
  AgentRegistryState,
  AgentStatus,
  AuraBrief,
  CommandAuraState,
  ConfidenceLevel,
  DecisionRoomState,
  DecisionTopicStatus,
  DomainState,
  ExecutionState,
  HealthStatus,
  MemoryStatus,
  RuntimeStatus,
} from "@/lib/aura/types";
import {
  mockCommandAuraState,
  mockDecisionRoomState,
  mockOperationalState,
} from "@/lib/aura/mock-data";

type TopBarMetric = {
  label: string;
  value: string;
  status?: "verified" | "stable" | "synced" | "warning" | "critical" | "default";
};

type TruthMetric = {
  label: string;
  value: string;
  status:
    | "blocker"
    | "pending"
    | "active"
    | "synced"
    | "incomplete"
    | "paused"
    | "waiting"
    | "stable"
    | "warning"
    | "critical";
};

type FutureSurface = {
  id:
    | "review-evidence"
    | "approve-execution"
    | "hold-deployment"
    | "deep-context"
    | "truth-detail"
    | "readiness-detail"
    | "codex-queue"
    | "claude-review"
    | "audit-trail";
  title: string;
  description: string;
  primaryMetric: string;
  evidenceItems: string[];
  actions: string[];
};

export type OperationalStateContract = {
  topBar: {
    environment: TopBarMetric;
    runtime: TopBarMetric;
    memory: TopBarMetric;
    provider: TopBarMetric;
    branch: TopBarMetric;
    health: TopBarMetric;
  };
  auraBrief: AuraBrief;
  truthRail: {
    blockers: TruthMetric;
    approvals: TruthMetric;
    activeJobs: TruthMetric;
    memory: TruthMetric;
    readiness: TruthMetric;
    codexQueue: TruthMetric;
    claudeReview: TruthMetric;
    runtime: TruthMetric;
    deepContext: {
      label: string;
      value: string;
    };
  };
  agents: AgentStatus[];
  jobs: ActiveJob[];
  commandThreads: CommandAuraState;
  decisionRoom: DecisionRoomState;
  executionState: ExecutionState;
  currentUser: {
    id: string;
    name: string;
    initials: string;
    role: string;
  };
  futureSurfaces: FutureSurface[];
  agentRegistry: AgentRegistryState;
};

const titleCase = (value: string) =>
  value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const runtimeTopBarStatus = (
  status: RuntimeStatus
): TopBarMetric["status"] => {
  if (status === "stable") return "stable";
  if (status === "degraded" || status === "initializing") return "warning";
  if (status === "error") return "critical";
  return "default";
};

const memoryTopBarStatus = (status: MemoryStatus): TopBarMetric["status"] => {
  if (status === "synced") return "synced";
  if (status === "syncing" || status === "stale") return "warning";
  if (status === "error") return "critical";
  return "default";
};

const healthTopBarStatus = (status: HealthStatus): TopBarMetric["status"] => {
  if (status === "verified") return "verified";
  if (status === "warning" || status === "unknown") return "warning";
  if (status === "critical") return "critical";
  return "default";
};

const truthStatusFromDomain = (domain: DomainState): TruthMetric["status"] => {
  if (domain.status === "complete" || domain.status === "stable") return "stable";
  if (domain.status === "active" || domain.status === "in-progress") return "active";
  if (domain.status === "incomplete") return "incomplete";
  if (domain.status === "paused") return "paused";
  if (domain.status === "waiting") return "waiting";
  if (domain.status === "blocked" || domain.status === "error") return "blocker";
  return "waiting";
};

const findDomain = (domain: DomainState["domain"]) =>
  mockOperationalState.truth.domainStates.find((state) => state.domain === domain);

const readinessDomain =
  mockOperationalState.truth.domainStates.find((state) => state.status === "incomplete") ??
  mockOperationalState.truth.domainStates[0];
const codexDomain = findDomain("codex-jobs");
const claudeDomain = findDomain("claude-reviews");
const runtimeDomain = findDomain("runtime");

const readinessConfidenceLabel = (
  level: ConfidenceLevel,
  score: number,
  verifiedChecks: number,
  totalChecks: number
) => `${titleCase(level)} · ${score}% · ${verifiedChecks}/${totalChecks}`;

export const operationalState: OperationalStateContract = {
  topBar: {
    environment: {
      label: "Environment",
      value: titleCase(mockOperationalState.system.environment),
      status: "default",
    },
    runtime: {
      label: "Runtime",
      value: titleCase(mockOperationalState.system.runtime),
      status: runtimeTopBarStatus(mockOperationalState.system.runtime),
    },
    memory: {
      label: "Memory",
      value: titleCase(mockOperationalState.system.memory),
      status: memoryTopBarStatus(mockOperationalState.system.memory),
    },
    provider: {
      label: "Provider",
      value: titleCase(mockOperationalState.system.provider),
      status: "default",
    },
    branch: {
      label: "Branch",
      value: mockOperationalState.system.branch,
      status: "default",
    },
    health: {
      label: "Health",
      value: titleCase(mockOperationalState.system.health),
      status: healthTopBarStatus(mockOperationalState.system.health),
    },
  },
  auraBrief: mockOperationalState.brief,
  truthRail: {
    blockers: {
      label: "Blockers",
      value: `${mockOperationalState.truth.counts.blockers} active`,
      status: mockOperationalState.truth.counts.blockers > 0 ? "blocker" : "stable",
    },
    approvals: {
      label: "Approvals",
      value: `${mockOperationalState.truth.counts.pendingApprovals} pending`,
      status: mockOperationalState.truth.counts.pendingApprovals > 0 ? "pending" : "stable",
    },
    activeJobs: {
      label: "Active jobs",
      value: "0",
      status: "stable",
    },
    memory: {
      label: "Memory",
      value: titleCase(mockOperationalState.truth.memoryStatus),
      status: mockOperationalState.truth.memoryStatus === "synced" ? "synced" : "warning",
    },
    readiness: {
      label: "Readiness",
      value: readinessConfidenceLabel(
        mockOperationalState.brief.evidenceConfidence.level,
        mockOperationalState.brief.evidenceConfidence.score,
        mockOperationalState.brief.evidenceConfidence.verifiedChecks,
        mockOperationalState.brief.evidenceConfidence.totalChecks
      ),
      status: readinessDomain ? truthStatusFromDomain(readinessDomain) : "waiting",
    },
    codexQueue: {
      label: codexDomain?.label ?? "Codex queue",
      value: codexDomain?.statusLabel ?? "Unknown",
      status: codexDomain ? truthStatusFromDomain(codexDomain) : "waiting",
    },
    claudeReview: {
      label: claudeDomain?.label ?? "Claude review",
      value: claudeDomain?.statusLabel ?? "Unknown",
      status: claudeDomain ? truthStatusFromDomain(claudeDomain) : "waiting",
    },
    runtime: {
      label: runtimeDomain?.label ?? "Runtime",
      value: runtimeDomain?.statusLabel ?? titleCase(mockOperationalState.truth.runtimeStatus),
      status: runtimeDomain ? truthStatusFromDomain(runtimeDomain) : "stable",
    },
    deepContext: {
      label: "Deep Context",
      value: "available",
    },
  },
  agents: mockOperationalState.agents,
  jobs: [],
  commandThreads: mockCommandAuraState,
  decisionRoom: mockDecisionRoomState,
  executionState: mockOperationalState.executionState,
  currentUser: mockOperationalState.currentUser,
  futureSurfaces: [
    {
      id: "review-evidence",
      title: "Review Evidence",
      description:
        "Evidence attached to the active operating decision, grouped for review before approval.",
      primaryMetric: operationalStateSafeEvidenceMetric(),
      evidenceItems: mockDecisionRoomState.intelligence?.evidenceItems.map((item) => item.title) ?? [],
      actions: ["Open Decision Room", "Request Claude Review", "Record Evidence Note"],
    },
    {
      id: "approve-execution",
      title: "Approve Execution",
      description:
        "Approval surface for execution paths that AURA considers eligible or partially eligible.",
      primaryMetric: `${mockOperationalState.brief.executionReadiness.score}% readiness`,
      evidenceItems: mockOperationalState.brief.executionReadiness.safeActions,
      actions: ["Approve Safe Action", "Hold Production", "Ask AURA"],
    },
    {
      id: "hold-deployment",
      title: "Hold Deployment",
      description:
        "Governed hold for production-impacting actions while safe implementation and review continue.",
      primaryMetric: `${mockOperationalState.brief.executionReadiness.blockedActions.length} blocked actions`,
      evidenceItems: mockOperationalState.brief.executionReadiness.blockedActions,
      actions: ["Record Hold", "Open Decision Room", "Request Review"],
    },
    {
      id: "deep-context",
      title: "Deep Context Evidence",
      description:
        "Collapsed runtime context for deeper inspection without flooding the operational console.",
      primaryMetric: mockOperationalState.truth.deepContextExpanded ? "Expanded" : "Available",
      evidenceItems: mockCommandAuraState.context.memoryNotes,
      actions: ["Open Audit Trail", "Open Memory Notes", "Return to Console"],
    },
    {
      id: "truth-detail",
      title: "Operational Truth Detail",
      description:
        "Expanded operational state behind the right rail, without raw logs on the main screen.",
      primaryMetric: `${mockOperationalState.truth.counts.blockers} blockers · ${mockOperationalState.truth.counts.pendingApprovals} approvals`,
      evidenceItems: mockOperationalState.truth.domainStates.map(
        (state) => `${state.label}: ${state.statusLabel}`
      ),
      actions: ["Review Evidence", "Open Decision Room", "Command AURA"],
    },
    {
      id: "readiness-detail",
      title: "Readiness Detail",
      description:
        "Execution readiness across local implementation, review state, and production-impacting constraints.",
      primaryMetric: `${mockOperationalState.brief.executionReadiness.score}%`,
      evidenceItems: [
        ...mockOperationalState.brief.executionReadiness.safeActions,
        ...mockOperationalState.brief.executionReadiness.blockers,
      ],
      actions: ["Approve Execution", "Hold Deployment", "Request Claude Review"],
    },
    {
      id: "codex-queue",
      title: "Codex Queue / Execution Jobs",
      description:
        "Full execution queue for implementation work, worktrees, branch state, and reviewer ownership.",
      primaryMetric: `${mockOperationalState.jobs.length} jobs tracked`,
      evidenceItems: mockOperationalState.jobs.map(
        (job) => `${job.title} · ${titleCase(job.status)} · ${titleCase(job.riskLevel)} risk`
      ),
      actions: ["Open Job", "Open Worktree", "Open Decision Room"],
    },
    {
      id: "claude-review",
      title: "Claude Review / Reviewer State",
      description:
        "Reviewer state for decisions that require independent audit before closure or approval.",
      primaryMetric: claudeDomain?.statusLabel ?? "Unknown",
      evidenceItems:
        mockDecisionRoomState.intelligence?.risk.factors ?? ["No reviewer risk factors available"],
      actions: ["Request Claude Review", "Attach Evidence", "Record Through Core"],
    },
    {
      id: "audit-trail",
      title: "Audit Trail / Runtime Evidence",
      description:
        "Governed history of decisions, evidence, messages, and Core persistence events.",
      primaryMetric: `${mockDecisionRoomState.intelligence?.history.length ?? 0} events`,
      evidenceItems:
        mockDecisionRoomState.intelligence?.history.map(
          (entry) => `${entry.action} · ${entry.actor}`
        ) ?? [],
      actions: ["Open Decision Room", "Review Evidence", "Export Evidence"],
    },
  ],
  agentRegistry: {
    agents: buildAgentRegistry(),
    selectedAgentId: null,
    filterStatus: "all",
    filterKind: "all",
  },
};

function operationalStateSafeEvidenceMetric() {
  const confidence = mockOperationalState.brief.evidenceConfidence;
  return `${titleCase(confidence.level)} confidence · ${confidence.verifiedChecks}/${confidence.totalChecks} checks`;
}

function buildAgentRegistry(): AgentProfile[] {
  const now = "2026-05-15T00:00:00.000Z";

  return [
    {
      identity: {
        id: "agent-aura",
        displayName: "AURA",
        kind: "ai",
        domain: "climb-one",
      },
      capability: {
        role: "supervisor",
        strengths: [
          "Operational judgment",
          "Decision synthesis",
          "Approval governance",
          "Memory continuity",
        ],
        limitations: [
          "Routes execution through Core",
          "Requires governed connectors for external actions",
        ],
        allowedSurfaces: ["command-aura", "decision-room", "founder-supervisor"],
      },
      intelligence: {
        providerType: "openai",
        modelLabel: "Provider adapter",
        configStatus: "configured",
      },
      authority: {
        allowedActions: [
          "Assess operational truth",
          "Recommend next actions",
          "Coordinate agents",
          "Persist decisions through Core",
        ],
        blockedActions: ["Ungoverned secret access", "Ungated production impact"],
        approvalRequiredFor: {
          externalSystems: true,
          secrets: true,
          productionImpact: true,
          connectorWrites: true,
        },
        auditorCounterpart: "agent-claude",
      },
      memory: {
        scope: "tenant",
        retentionDays: 365,
      },
      tools: [
        {
          connectorType: "api",
          label: "CLIMB ONE Core",
          enabled: true,
          requiresCoreApproval: false,
        },
      ],
      registrationStatus: "live",
      registeredAt: now,
      lastActiveAt: now,
      createdAt: now,
      updatedAt: now,
    },
    {
      identity: {
        id: "agent-founder",
        displayName: "Founder Agent",
        kind: "ai",
        domain: "climb-one",
      },
      capability: {
        role: "supervisor",
        strengths: [
          "Runtime supervision",
          "Operational reconciliation",
          "Worktree coordination",
          "Closure discipline",
        ],
        limitations: [
          "Must preserve Core governance",
          "Cannot bypass evidence requirements",
        ],
        allowedSurfaces: ["command-aura", "decision-room", "founder-supervisor"],
      },
      intelligence: {
        providerType: "openai",
        modelLabel: "Supervisor adapter",
        configStatus: "configured",
      },
      authority: {
        allowedActions: [
          "Supervise execution",
          "Route worker lanes",
          "Ask peer intelligence",
          "Record learning through Core",
        ],
        blockedActions: ["Silent production execution", "Unreviewed scope expansion"],
        approvalRequiredFor: {
          externalSystems: true,
          secrets: true,
          productionImpact: true,
          connectorWrites: true,
        },
        auditorCounterpart: "agent-aura",
      },
      memory: {
        scope: "workspace",
        retentionDays: 365,
      },
      tools: [
        {
          connectorType: "api",
          label: "Postgres runtime state",
          enabled: true,
          requiresCoreApproval: false,
        },
      ],
      registrationStatus: "live",
      registeredAt: now,
      lastActiveAt: now,
      createdAt: now,
      updatedAt: now,
    },
    {
      identity: {
        id: "agent-codex",
        displayName: "Codex",
        kind: "ai",
        domain: "climb-one",
      },
      capability: {
        role: "executor",
        strengths: ["Code implementation", "Repository inspection", "Test execution", "Git workflow"],
        limitations: ["Cannot become canonical memory", "External writes require Core policy"],
        allowedSurfaces: ["command-aura", "decision-room", "tools"],
      },
      intelligence: {
        providerType: "openai",
        modelLabel: "Codex CLI",
        configStatus: "configured",
      },
      authority: {
        allowedActions: ["Implement code", "Run tests", "Prepare commits", "Report evidence"],
        blockedActions: ["Production deployment without approval"],
        approvalRequiredFor: {
          externalSystems: true,
          secrets: true,
          productionImpact: true,
          connectorWrites: true,
        },
        auditorCounterpart: "agent-claude",
      },
      memory: {
        scope: "workspace",
        retentionDays: 90,
      },
      tools: [
        {
          connectorType: "api",
          label: "GitHub repository",
          enabled: true,
          requiresCoreApproval: false,
        },
      ],
      registrationStatus: "live",
      registeredAt: now,
      lastActiveAt: now,
      createdAt: now,
      updatedAt: now,
    },
    {
      identity: {
        id: "agent-claude",
        displayName: "Claude",
        kind: "ai",
        domain: "governance",
      },
      capability: {
        role: "reviewer",
        strengths: ["Independent critique", "Risk review", "Architecture review", "Decision challenge"],
        limitations: ["Reviewer path depends on configured provider access"],
        allowedSurfaces: ["command-aura", "decision-room"],
      },
      intelligence: {
        providerType: "anthropic",
        modelLabel: "Reviewer adapter",
        configStatus: "draft",
      },
      authority: {
        allowedActions: ["Review decisions", "Challenge scope", "Assess risk"],
        blockedActions: ["Direct execution", "Production changes"],
        approvalRequiredFor: {
          externalSystems: false,
          secrets: false,
          productionImpact: false,
          connectorWrites: false,
        },
      },
      memory: {
        scope: "session",
      },
      tools: [],
      registrationStatus: "requires-core-handler",
      createdAt: now,
      updatedAt: now,
    },
    {
      identity: {
        id: "agent-sap-operator",
        displayName: "SAP Operator",
        kind: "connector-operator",
        domain: "sap",
      },
      capability: {
        role: "connector-operator",
        strengths: ["SAP GUI flow capture", "BTP/BAS browser operation", "Integration Suite evidence"],
        limitations: ["Requires on-prem connector", "Requires credential references, not raw secrets"],
        allowedSurfaces: ["tools", "sap-pack", "decision-room"],
      },
      intelligence: {
        providerType: "not-configured",
        configStatus: "draft",
      },
      authority: {
        allowedActions: ["Observe screens", "Capture evidence", "Prepare connector runs"],
        blockedActions: ["Credential disclosure", "Unapproved production writes"],
        approvalRequiredFor: {
          externalSystems: true,
          secrets: true,
          productionImpact: true,
          connectorWrites: true,
        },
        auditorCounterpart: "agent-aura",
      },
      memory: {
        scope: "domain-pack",
      },
      tools: [
        {
          connectorType: "sap",
          label: "SAP GUI bridge",
          enabled: false,
          requiresCoreApproval: true,
          credentialReference: "required",
        },
        {
          connectorType: "integration-suite",
          label: "Integration Suite browser connector",
          enabled: false,
          requiresCoreApproval: true,
          credentialReference: "required",
        },
        {
          connectorType: "bas",
          label: "SAP BAS browser connector",
          enabled: false,
          requiresCoreApproval: true,
          credentialReference: "required",
        },
      ],
      registrationStatus: "pending-core-registration",
      createdAt: now,
      updatedAt: now,
    },
  ];
}

export {
  mockCommandAuraState,
  mockDecisionRoomState,
  mockOperationalState,
};
